import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { ThemeProps, SelectableThemeProps } from '@theme/base';
import { isTablet, isMobile, mq } from '@styles/breakpoints';
import { BaseStyledTextarea } from '@styles/common';
import { Pill } from '@components/search/destinations/suggested-destinations/Pill';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { Name } from '@model/iceberg';
import { Destination } from '@model/state';
import { Icons } from '@model/common';
import { isHotelPath } from '@util/path';
import { MAXIMUM_DESTINATIONS } from '@model/price-calendar';
import { Icon } from '@components/common/generic-icon';

const SelectionContainer: any = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group2.lighter,
  paddingLeft: theme.custom.spacing.medium,
  paddingRight: theme.custom.spacing.medium,
  paddingTop: theme.custom.spacing.medium,
  [mq.medium]: {
    width: '100%'
  }
}));

const InputContainer: any = styled.div(({ theme, isSelected }: SelectableThemeProps) => ({
  background: theme.custom.colors.white,
  border: `1px solid ${isSelected ? theme.custom.colors.group1.base : theme.custom.colors.group10.light}`,
  borderRadius: 10,
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 0%',
  flexWrap: 'wrap',
  padding: theme.custom.spacing.small
}));

const SearchIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.8rem',
  padding: `0 ${theme.custom.spacing.small}px`
}));

const StyledInput = styled(BaseStyledTextarea)({
  minWidth: 180
});

const StyledPill = styled(Pill)({
  margin: 2,
  height: 'fit-content'
});

const Separator: any = styled.hr(({ theme }: ThemeProps) => ({
  border: 'none',
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  margin: `${theme.custom.spacing.medium}px -${theme.custom.spacing.medium}px ${theme.custom.spacing.medium}px`,
  marginBottom: 0
}));

const Error: any = styled.div(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group11.base,
  ...(theme.custom.typography.labels.default as any),
  marginBottom: theme.custom.spacing.small
}));

export interface HeadingProps {
  input: string;
  values: Array<Destination>;
  hasMaximumDestinations: boolean;
  onChange: (payload: string) => void;
  onRemove: (payload: Destination) => void;
}

const getPlaceholder = (values: Array<Destination>) => {
  if (values.length) {
    if (values.length === 1) {
      if (isHotelPath(values[0].path)) {
        return '';
      }
    }
    if (values.length === MAXIMUM_DESTINATIONS) {
      return '';
    }
    return 'search__destination--add-another';
  }
  return 'search__destination--enter-destination';
};

export const Heading = ({ input, values, hasMaximumDestinations, onChange, onRemove }: HeadingProps) => {
  const ref: any = useRef();
  const t: any = useI18NextContext();
  const handleOnChange = (event: any) => onChange(event.target.value);
  const inputPlaceholder: string = getPlaceholder(values);
  const isHotelSearch: boolean = values.length === 1 && isHotelPath(values[0].path);
  useEffect(() => {
    if (!isTablet() && !isMobile()) {
      ref.current?.focus();
    }
  }, [values]);

  return (
    <SelectionContainer data-testid={TestId.dealFinder.destination.heading}>
      {hasMaximumDestinations && <Error>{t('search__destination--maximum')}</Error>}
      <InputContainer onClick={() => ref.current?.focus()} isSelected>
        <SearchIcon name={Icons.SEARCH} />
        {values.map((option: Name) => (
          <StyledPill key={option.display} display={option.display} value={option} onClear={onRemove} />
        ))}
        {!isHotelSearch && (
          <StyledInput ref={ref} value={input} onChange={handleOnChange} placeholder={t(inputPlaceholder)} />
        )}
      </InputContainer>
      <Separator />
    </SelectionContainer>
  );
};
